import React, { useState, useEffect } from 'react';
import { schoolProgramAPI } from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { LandingImageUpload, CarouselImageUpload } from '../components/LandingImageUpload';
import { 
  School, Search, Download, Trash2, Edit, Eye, Filter, 
  Settings, Users, Calendar, MapPin, Phone, Mail, 
  CheckCircle, Clock, XCircle, RefreshCw 
} from 'lucide-react';

const SchoolRegistrations = () => {
  const [registrations, setRegistrations] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    region: '',
    program_type: '',
    search: ''
  });
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [editData, setEditData] = useState({});
  const [configData, setConfigData] = useState({});

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-700',
    contacted: 'bg-blue-100 text-blue-700',
    confirmed: 'bg-green-100 text-green-700',
    cancelled: 'bg-red-100 text-red-700'
  };

  const statusIcons = {
    pending: <Clock className="w-3 h-3" />,
    contacted: <Phone className="w-3 h-3" />,
    confirmed: <CheckCircle className="w-3 h-3" />,
    cancelled: <XCircle className="w-3 h-3" />
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchRegistrations();
  }, [filters.status, filters.region, filters.program_type]);

  const fetchData = async () => {
    await Promise.all([fetchConfig(), fetchRegistrations()]);
    setLoading(false);
  };

  const fetchConfig = async () => {
    try {
      const response = await schoolProgramAPI.getConfig();
      setConfig(response.data);
      setConfigData(response.data);
    } catch (error) {
      console.error('Failed to fetch config:', error);
    }
  };

  const fetchRegistrations = async () => {
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.region) params.region = filters.region;
      if (filters.program_type) params.program_type = filters.program_type;
      if (filters.search) params.search = filters.search;
      
      const response = await schoolProgramAPI.getRegistrations(params);
      setRegistrations(response.data);
    } catch (error) {
      console.error('Failed to fetch registrations:', error);
      toast.error('Failed to load registrations');
    }
  };

  const handleSearch = () => {
    fetchRegistrations();
  };

  const handleView = (registration) => {
    setSelectedRegistration(registration);
    setShowViewDialog(true);
  };

  const handleEdit = (registration) => {
    setSelectedRegistration(registration);
    setEditData({ ...registration });
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    try {
      await schoolProgramAPI.updateRegistration(selectedRegistration.id, editData);
      toast.success('Registration updated successfully');
      setShowEditDialog(false);
      fetchRegistrations();
    } catch (error) {
      toast.error('Failed to update registration');
    }
  };

  const handleDelete = async (registration) => {
    if (!window.confirm(`Are you sure you want to delete the registration for ${registration.school_name}?`)) {
      return;
    }
    
    try {
      await schoolProgramAPI.deleteRegistration(registration.id);
      toast.success('Registration deleted');
      fetchRegistrations();
    } catch (error) {
      toast.error('Failed to delete registration');
    }
  };

  const handleExportCSV = async () => {
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.region) params.region = filters.region;
      if (filters.program_type) params.program_type = filters.program_type;
      
      const response = await schoolProgramAPI.exportCSV(params);
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `school_registrations_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('CSV exported successfully');
    } catch (error) {
      toast.error('Failed to export CSV');
    }
  };

  const handleSaveConfig = async () => {
    try {
      await schoolProgramAPI.updateConfig(configData);
      toast.success('Configuration saved');
      setShowConfigDialog(false);
      fetchConfig();
    } catch (error) {
      toast.error('Failed to save configuration');
    }
  };

  const filteredRegistrations = filters.search 
    ? registrations.filter(r => 
        r.school_name?.toLowerCase().includes(filters.search.toLowerCase()) ||
        r.contact_person_name?.toLowerCase().includes(filters.search.toLowerCase()) ||
        r.contact_email?.toLowerCase().includes(filters.search.toLowerCase())
      )
    : registrations;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="school-registrations-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <School className="w-6 h-6 text-teal-600" />
            School Registrations
          </h1>
          <p className="text-slate-500">Manage school program registrations</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowConfigDialog(true)} data-testid="config-btn">
            <Settings className="w-4 h-4 mr-2" /> Settings
          </Button>
          <Button variant="outline" onClick={handleExportCSV} data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total</p>
                <p className="text-2xl font-bold">{registrations.length}</p>
              </div>
              <Users className="w-8 h-8 text-slate-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Pending</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {registrations.filter(r => r.status === 'pending').length}
                </p>
              </div>
              <Clock className="w-8 h-8 text-yellow-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Confirmed</p>
                <p className="text-2xl font-bold text-green-600">
                  {registrations.filter(r => r.status === 'confirmed').length}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Students</p>
                <p className="text-2xl font-bold text-teal-600">
                  {registrations.reduce((sum, r) => sum + (r.number_of_students || 0), 0)}
                </p>
              </div>
              <School className="w-8 h-8 text-teal-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search schools, contacts..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-9"
                  data-testid="search-input"
                />
              </div>
            </div>
            <div className="w-[150px]">
              <Label className="text-xs">Status</Label>
              <Select value={filters.status || "all"} onValueChange={(v) => setFilters(prev => ({ ...prev, status: v === "all" ? "" : v }))}>
                <SelectTrigger data-testid="status-filter">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-[150px]">
              <Label className="text-xs">Region</Label>
              <Select value={filters.region || "all"} onValueChange={(v) => setFilters(prev => ({ ...prev, region: v === "all" ? "" : v }))}>
                <SelectTrigger data-testid="region-filter">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {config?.regions?.map(region => (
                    <SelectItem key={region} value={region}>{region}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-[150px]">
              <Label className="text-xs">Program</Label>
              <Select value={filters.program_type || "all"} onValueChange={(v) => setFilters(prev => ({ ...prev, program_type: v === "all" ? "" : v }))}>
                <SelectTrigger data-testid="program-filter">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {config?.program_types?.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={() => setFilters({ status: '', region: '', program_type: '', search: '' })}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Registrations Table */}
      <Card>
        <CardContent className="pt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-3 px-2">School</th>
                <th className="text-left py-3 px-2">Contact</th>
                <th className="text-left py-3 px-2">Students</th>
                <th className="text-left py-3 px-2">Program</th>
                <th className="text-left py-3 px-2">Region</th>
                <th className="text-left py-3 px-2">Price</th>
                <th className="text-left py-3 px-2">Status</th>
                <th className="text-left py-3 px-2">Date</th>
                <th className="text-right py-3 px-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRegistrations.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center py-8 text-slate-500">
                    No registrations found
                  </td>
                </tr>
              ) : (
                filteredRegistrations.map((reg) => (
                  <tr key={reg.id} className="border-b hover:bg-slate-50" data-testid={`registration-${reg.id}`}>
                    <td className="py-3 px-2">
                      <div>
                        <p className="font-medium">{reg.school_name}</p>
                        <p className="text-xs text-slate-500">{reg.school_type}</p>
                      </div>
                    </td>
                    <td className="py-3 px-2">
                      <div>
                        <p>{reg.contact_person_name}</p>
                        <p className="text-xs text-slate-500">{reg.contact_email}</p>
                      </div>
                    </td>
                    <td className="py-3 px-2 font-medium">{reg.number_of_students}</td>
                    <td className="py-3 px-2">{reg.program_type}</td>
                    <td className="py-3 px-2">{reg.region}</td>
                    <td className="py-3 px-2">E{reg.calculated_price?.toLocaleString()}</td>
                    <td className="py-3 px-2">
                      <Badge className={statusColors[reg.status] || 'bg-slate-100'}>
                        {statusIcons[reg.status]} <span className="ml-1">{reg.status}</span>
                      </Badge>
                    </td>
                    <td className="py-3 px-2 text-slate-500">
                      {reg.created_at?.split('T')[0]}
                    </td>
                    <td className="py-3 px-2">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => handleView(reg)} data-testid={`view-${reg.id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleEdit(reg)} data-testid={`edit-${reg.id}`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(reg)} className="text-red-500 hover:text-red-700" data-testid={`delete-${reg.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* View Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Registration Details</DialogTitle>
            <DialogDescription>{selectedRegistration?.school_name}</DialogDescription>
          </DialogHeader>
          {selectedRegistration && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">School Name</Label>
                  <p className="font-medium">{selectedRegistration.school_name}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">School Type</Label>
                  <p>{selectedRegistration.school_type}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Region</Label>
                  <p>{selectedRegistration.region}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Address</Label>
                  <p>{selectedRegistration.address}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Contact Person</Label>
                  <p>{selectedRegistration.contact_person_name}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Email</Label>
                  <p>{selectedRegistration.contact_email}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Phone</Label>
                  <p>{selectedRegistration.contact_phone}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Number of Students</Label>
                  <p className="font-bold text-lg">{selectedRegistration.number_of_students}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Program Type</Label>
                  <p>{selectedRegistration.program_type}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Calculated Price</Label>
                  <p className="font-bold text-lg text-teal-600">E{selectedRegistration.calculated_price?.toLocaleString()}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Grade Levels</Label>
                  <p>{selectedRegistration.grade_levels?.join(', ')}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Preferred Days</Label>
                  <p>{selectedRegistration.preferred_days?.join(', ')}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Preferred Times</Label>
                  <p>{selectedRegistration.preferred_times || 'Not specified'}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Pool Access</Label>
                  <p>{selectedRegistration.pool_access ? 'Yes' : 'No'}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Transportation</Label>
                  <p>{selectedRegistration.transportation_assistance ? 'Needed' : 'Not needed'}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Status</Label>
                  <Badge className={statusColors[selectedRegistration.status]}>
                    {selectedRegistration.status}
                  </Badge>
                </div>
              </div>
              {selectedRegistration.notes && (
                <div>
                  <Label className="text-xs text-slate-500">Notes</Label>
                  <p className="bg-slate-50 p-3 rounded">{selectedRegistration.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Registration</DialogTitle>
            <DialogDescription>{editData?.school_name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>School Name</Label>
                <Input
                  value={editData.school_name || ''}
                  onChange={(e) => setEditData(prev => ({ ...prev, school_name: e.target.value }))}
                />
              </div>
              <div>
                <Label>School Type</Label>
                <Select value={editData.school_type} onValueChange={(v) => setEditData(prev => ({ ...prev, school_type: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {config?.school_types?.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Contact Person</Label>
                <Input
                  value={editData.contact_person_name || ''}
                  onChange={(e) => setEditData(prev => ({ ...prev, contact_person_name: e.target.value }))}
                />
              </div>
              <div>
                <Label>Contact Role</Label>
                <Select value={editData.contact_person_role || ''} onValueChange={(v) => setEditData(prev => ({ ...prev, contact_person_role: v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {(config?.contact_roles || ['Principal', 'Administrator', 'Teacher', 'Sports Coordinator', 'Other']).map(role => (
                      <SelectItem key={role} value={role}>{role}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Contact Email</Label>
                <Input
                  type="email"
                  value={editData.contact_email || ''}
                  onChange={(e) => setEditData(prev => ({ ...prev, contact_email: e.target.value }))}
                />
              </div>
              <div>
                <Label>Contact Phone</Label>
                <Input
                  value={editData.contact_phone || ''}
                  onChange={(e) => setEditData(prev => ({ ...prev, contact_phone: e.target.value }))}
                />
              </div>
              <div>
                <Label>Number of Students</Label>
                <Input
                  type="number"
                  value={editData.number_of_students || ''}
                  onChange={(e) => setEditData(prev => ({ ...prev, number_of_students: parseInt(e.target.value) }))}
                />
              </div>
              <div>
                <Label>Region</Label>
                <Select value={editData.region} onValueChange={(v) => setEditData(prev => ({ ...prev, region: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {config?.regions?.map(region => (
                      <SelectItem key={region} value={region}>{region}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Preferred Session Date</Label>
                <Input
                  type="date"
                  value={editData.preferred_session_date || ''}
                  onChange={(e) => setEditData(prev => ({ ...prev, preferred_session_date: e.target.value }))}
                />
              </div>
              <div>
                <Label>Preferred Session Time</Label>
                <Input
                  value={editData.preferred_session_time || ''}
                  onChange={(e) => setEditData(prev => ({ ...prev, preferred_session_time: e.target.value }))}
                />
              </div>
              <div className="col-span-2">
                <Label>Status</Label>
                <Select value={editData.status} onValueChange={(v) => setEditData(prev => ({ ...prev, status: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>School Notes</Label>
              <Textarea
                value={editData.notes || ''}
                onChange={(e) => setEditData(prev => ({ ...prev, notes: e.target.value }))}
                rows={2}
                placeholder="Notes from the school..."
              />
            </div>
            <div>
              <Label>Admin Notes</Label>
              <Textarea
                value={editData.admin_notes || ''}
                onChange={(e) => setEditData(prev => ({ ...prev, admin_notes: e.target.value }))}
                rows={2}
                placeholder="Internal notes for admin use..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveEdit} className="bg-teal-600 hover:bg-teal-700">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Config Dialog */}
      <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Schools Program Settings</DialogTitle>
            <DialogDescription>Configure program options, pricing, banner and marketing</DialogDescription>
          </DialogHeader>
          <Tabs defaultValue="general">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="general">General</TabsTrigger>
              <TabsTrigger value="pricing">Pricing</TabsTrigger>
              <TabsTrigger value="banner">Banner</TabsTrigger>
              <TabsTrigger value="options">Options</TabsTrigger>
            </TabsList>
            <TabsContent value="general" className="space-y-4">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={configData.enabled}
                  onChange={(e) => setConfigData(prev => ({ ...prev, enabled: e.target.checked }))}
                  className="w-4 h-4"
                />
                <Label>Program Enabled</Label>
              </div>
              <div>
                <Label>Title</Label>
                <Input
                  value={configData.title || ''}
                  onChange={(e) => setConfigData(prev => ({ ...prev, title: e.target.value }))}
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={configData.description || ''}
                  onChange={(e) => setConfigData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                />
              </div>
              <div>
                <Label>Highlights (comma-separated)</Label>
                <Textarea
                  value={configData.highlights?.join(', ') || ''}
                  onChange={(e) => setConfigData(prev => ({ 
                    ...prev, 
                    highlights: e.target.value.split(',').map(s => s.trim()).filter(Boolean) 
                  }))}
                  rows={3}
                />
              </div>
            </TabsContent>
            <TabsContent value="pricing" className="space-y-4">
              <div>
                <Label>Price Per Student (E)</Label>
                <Input
                  type="number"
                  value={configData.per_student_price || ''}
                  onChange={(e) => setConfigData(prev => ({ ...prev, per_student_price: parseFloat(e.target.value) }))}
                />
              </div>
              <div>
                <Label>Discount Threshold (students)</Label>
                <Input
                  type="number"
                  value={configData.discount_threshold || ''}
                  onChange={(e) => setConfigData(prev => ({ ...prev, discount_threshold: parseInt(e.target.value) }))}
                />
              </div>
              <div>
                <Label>Discount Percentage (%)</Label>
                <Input
                  type="number"
                  value={configData.discount_percentage || ''}
                  onChange={(e) => setConfigData(prev => ({ ...prev, discount_percentage: parseFloat(e.target.value) }))}
                />
              </div>
            </TabsContent>
            <TabsContent value="banner" className="space-y-4">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={configData.banner_enabled}
                  onChange={(e) => setConfigData(prev => ({ ...prev, banner_enabled: e.target.checked }))}
                  className="w-4 h-4"
                />
                <Label>Banner Enabled</Label>
              </div>
              <div>
                <Label>Banner Title</Label>
                <Input
                  value={configData.banner_title || ''}
                  onChange={(e) => setConfigData(prev => ({ ...prev, banner_title: e.target.value }))}
                  placeholder="Partner With Us"
                />
              </div>
              <div>
                <Label>Banner Subtitle</Label>
                <Input
                  value={configData.banner_subtitle || ''}
                  onChange={(e) => setConfigData(prev => ({ ...prev, banner_subtitle: e.target.value }))}
                  placeholder="Bring professional swimming instruction to your school"
                />
              </div>
              
              {/* Carousel Images - Upload UI */}
              <CarouselImageUpload
                currentImages={configData.carousel_images || []}
                onImagesChange={(images) => setConfigData(prev => ({ ...prev, carousel_images: images }))}
                imageType="schools_carousel"
                itemId="schools"
                label="Carousel Images"
                maxImages={10}
              />
              
              {/* Flyer Upload */}
              <LandingImageUpload
                currentImageUrl={configData.flyer_url || ''}
                imageType="schools_flyer"
                itemId="schools"
                onUploadSuccess={(url) => setConfigData(prev => ({ ...prev, flyer_url: url }))}
                label="Program Flyer (PDF/Image)"
                aspectRatio="portrait"
                showUrlInput={true}
              />
              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium mb-2">Marketing/Ad Settings</h4>
                <div className="flex items-center gap-2 mb-3">
                  <input
                    type="checkbox"
                    checked={configData.ads_enabled}
                    onChange={(e) => setConfigData(prev => ({ ...prev, ads_enabled: e.target.checked }))}
                    className="w-4 h-4"
                  />
                  <Label>Ads Enabled</Label>
                </div>
                <div className="mb-3">
                  <Label>Ad Text</Label>
                  <Input
                    value={configData.ad_text || ''}
                    onChange={(e) => setConfigData(prev => ({ ...prev, ad_text: e.target.value }))}
                    placeholder="Special rates for schools with 20+ students!"
                  />
                </div>
                <div>
                  <Label>CTA Button Text</Label>
                  <Input
                    value={configData.ad_cta_text || ''}
                    onChange={(e) => setConfigData(prev => ({ ...prev, ad_cta_text: e.target.value }))}
                    placeholder="Register Your School Today"
                  />
                </div>
              </div>
            </TabsContent>
            <TabsContent value="options" className="space-y-4">
              <div>
                <Label>Regions (comma-separated)</Label>
                <Textarea
                  value={configData.regions?.join(', ') || ''}
                  onChange={(e) => setConfigData(prev => ({ 
                    ...prev, 
                    regions: e.target.value.split(',').map(s => s.trim()).filter(Boolean) 
                  }))}
                />
              </div>
              <div>
                <Label>Grade Levels (comma-separated)</Label>
                <Textarea
                  value={configData.grade_levels?.join(', ') || ''}
                  onChange={(e) => setConfigData(prev => ({ 
                    ...prev, 
                    grade_levels: e.target.value.split(',').map(s => s.trim()).filter(Boolean) 
                  }))}
                />
              </div>
              <div>
                <Label>School Types (comma-separated)</Label>
                <Textarea
                  value={configData.school_types?.join(', ') || ''}
                  onChange={(e) => setConfigData(prev => ({ 
                    ...prev, 
                    school_types: e.target.value.split(',').map(s => s.trim()).filter(Boolean) 
                  }))}
                />
              </div>
              <div>
                <Label>Contact Roles (comma-separated)</Label>
                <Textarea
                  value={configData.contact_roles?.join(', ') || ''}
                  onChange={(e) => setConfigData(prev => ({ 
                    ...prev, 
                    contact_roles: e.target.value.split(',').map(s => s.trim()).filter(Boolean) 
                  }))}
                />
              </div>
              <div>
                <Label>Program Types (comma-separated)</Label>
                <Textarea
                  value={configData.program_types?.join(', ') || ''}
                  onChange={(e) => setConfigData(prev => ({ 
                    ...prev, 
                    program_types: e.target.value.split(',').map(s => s.trim()).filter(Boolean) 
                  }))}
                />
              </div>
            </TabsContent>
          </Tabs>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfigDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveConfig} className="bg-teal-600 hover:bg-teal-700">Save Settings</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SchoolRegistrations;
